import { api } from "#/lib/api-client";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizePage<T>(raw: any): PageResponse<T> {
  const body = raw?.content !== undefined ? raw : (raw?.data ?? raw);
  const meta = body?.page ?? body;
  return {
    content:       body?.content       ?? [],
    totalElements: meta?.totalElements ?? meta?.total_elements ?? meta?.total ?? 0,
    totalPages:    meta?.totalPages    ?? meta?.total_pages    ?? 0,
    number:        meta?.number        ?? 0,
    size:          meta?.size          ?? 0,
    first:         meta?.number === 0,
    last:          (meta?.number ?? 0) + 1 >= (meta?.totalPages ?? meta?.total_pages ?? 1),
  };
}

import type {
  CurrentEmployee,
  EligibleLeaveType,
  EmployeeOption,
  Holiday,
  HolidayCreateRequest,
  HolidayImportResponse,
  HolidayUpdateRequest,
  LeaveApplicationDetails,
  LeaveApplicationSummary,
  LeaveApplyRequest,
  LeaveApprovalRequest,
  LeaveBalance,
  LeaveBalanceAdjustmentRequest,
  LeaveBalanceMatrixResponse,
  LeaveDashboardSummaryResponse,
  LeaveBalanceRecalculationResponse,
  LeaveEntitlementRule,
  LeaveEntitlementRuleCreateRequest,
  LeaveEntitlementRuleUpdateRequest,
  LeaveRuleOption,
  LeavePlan,
  LeavePlanCreateRequest,
  LeavePlanStatus,
  LeavePlanUpdateRequest,
  LeaveRecallRequest,
  LeaveSetupStatus,
  LeaveType,
  LeaveTypeCreateRequest,
  LeaveTypeUpdateRequest,
  LeaveYearPolicy,
  LeaveYearPolicyCreateRequest,
  LeaveYearPolicyUpdateRequest,
  LeaveYearRolloverResult,
  OfficialDutyCreateRequest,
  OfficialDutyResponse,
  OfficialDutyUpdateRequest,
  PageResponse,
  RefLookup,
  ReliefEmployeeOption,
  WhoIsOnLeaveResponse,
} from "./types";

function buildQuery(
  params: Record<string, string | number | boolean | undefined | null>,
) {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.append(key, String(value));
    }
  });

  return query.toString();
}

export const leaveApi = {
  async setupStatus(): Promise<LeaveSetupStatus> {
    const response = await api.get<LeaveSetupStatus>(
      "/api/hr/leave/setup/status",
    );
    return response.data;
  },

  /**
   * Maps whatever leave types/entitlement rules are currently configured onto every active
   * employee by creating any missing leave balance rows for the current leave year. Safe to
   * run repeatedly -- existing balances are left untouched, this only fills in gaps.
   */
  async recalculateBalances(): Promise<LeaveBalanceRecalculationResponse> {
    const response = await api.post<LeaveBalanceRecalculationResponse>(
      "/api/hr/leave/setup/recalculate-balances",
    );
    return response.data;
  },

  async currentEmployee(): Promise<CurrentEmployee> {
    const response = await api.get<CurrentEmployee>("/api/hr/employees/me");
    return response.data;
  },

  async employeeOptions(): Promise<EmployeeOption[]> {
    const response = await api.get<EmployeeOption[]>(
      "/api/hr/employees/options",
    );
    return response.data;
  },

  async lookups(
    lookupType: string,
    activeOnly = true,
  ): Promise<RefLookup[]> {
    const query = buildQuery({ lookupType, activeOnly });
    const response = await api.get<RefLookup[]>(
      `/api/hr/ref/lookups?${query}`,
    );
    return response.data;
  },

  async genders(): Promise<RefLookup[]> {
    return this.lookups("GENDER", true);
  },

  async employmentTypes(): Promise<RefLookup[]> {
    return this.lookups("EMPLOYMENT_TYPE", true);
  },

  async employeeCategories(): Promise<RefLookup[]> {
    return this.lookups("EMPLOYEE_CATEGORY", true);
  },

  // Roles and departments for entitlement-rule scoping (e.g. "Department Head" role
  // gets a higher annual leave entitlement than normal staff). Reuses the same
  // lightweight options endpoints the Workflows and Employees features already use.
  async roles(): Promise<LeaveRuleOption[]> {
    const response = await api.get<LeaveRuleOption[]>("/api/hr/roles/options");
    return response.data;
  },

  async departments(): Promise<LeaveRuleOption[]> {
    const response = await api.get<LeaveRuleOption[]>("/api/hr/departments/options");
    return response.data;
  },

  async myApplications(
    page = 0,
    size = 10,
  ): Promise<PageResponse<LeaveApplicationSummary>> {
    const query = buildQuery({ page, size });
    const response = await api.get(`/api/hr/leave/applications/mine?${query}`);
    return normalizePage<LeaveApplicationSummary>(response.data);
  },

  async approvalInbox(
    page = 0,
    size = 10,
  ): Promise<PageResponse<LeaveApplicationSummary>> {
    const query = buildQuery({ page, size });
    const response = await api.get(`/api/hr/leave/applications/inbox?${query}`);
    return normalizePage<LeaveApplicationSummary>(response.data);
  },

  async pendingApplications(
    page = 0,
    size = 10,
  ): Promise<PageResponse<LeaveApplicationSummary>> {
    const query = buildQuery({ page, size });
    const response = await api.get(`/api/hr/leave/applications/pending?${query}`);
    return normalizePage<LeaveApplicationSummary>(response.data);
  },

  async getApplication(id: number): Promise<LeaveApplicationDetails> {
    const response = await api.get<LeaveApplicationDetails>(
      `/api/hr/leave/applications/${id}`,
    );
    return response.data;
  },

  async apply(payload: LeaveApplyRequest): Promise<LeaveApplicationDetails> {
    const response = await api.post<LeaveApplicationDetails>(
      "/api/hr/leave/applications",
      payload,
    );
    return response.data;
  },

  async saveDraftApplication(
    payload: LeaveApplyRequest,
  ): Promise<LeaveApplicationDetails> {
    const response = await api.post<LeaveApplicationDetails>(
      "/api/hr/leave/applications/draft",
      payload,
    );
    return response.data;
  },

  async updateDraftApplication(
    id: number,
    payload: LeaveApplyRequest,
  ): Promise<LeaveApplicationDetails> {
    const response = await api.put<LeaveApplicationDetails>(
      `/api/hr/leave/applications/${id}/draft`,
      payload,
    );
    return response.data;
  },

  async submitDraftApplication(
    id: number,
  ): Promise<LeaveApplicationDetails> {
    const response = await api.post<LeaveApplicationDetails>(
      `/api/hr/leave/applications/${id}/submit`,
    );
    return response.data;
  },

  async approve(
    id: number,
    payload: LeaveApprovalRequest,
  ): Promise<LeaveApplicationDetails> {
    const response = await api.post<LeaveApplicationDetails>(
      `/api/hr/leave/applications/${id}/approve`,
      payload,
    );
    return response.data;
  },

  async reject(
    id: number,
    payload: LeaveApprovalRequest,
  ): Promise<LeaveApplicationDetails> {
    const response = await api.post<LeaveApplicationDetails>(
      `/api/hr/leave/applications/${id}/reject`,
      payload,
    );
    return response.data;
  },

  async cancel(
    id: number,
    comments?: string,
  ): Promise<LeaveApplicationDetails> {
    const query = buildQuery({ comments });
    const response = await api.post<LeaveApplicationDetails>(
      `/api/hr/leave/applications/${id}/cancel${query ? `?${query}` : ""}`,
    );
    return response.data;
  },

  /**
   * HR-initiated recall — requires HR_LEAVE_MANAGE / HR_LEAVE_APPROVE / SUPER_ADMIN.
   * Status → RECALLED; unused days are credited back to the employee's balance.
   */
  async recall(
    id: number,
    payload: LeaveRecallRequest,
  ): Promise<LeaveApplicationDetails> {
    const response = await api.post<LeaveApplicationDetails>(
      `/api/hr/leave/applications/${id}/recall`,
      payload,
    );
    return response.data;
  },

  /**
   * Employee self-service early return — accessible by the employee or HR.
   * Status → RECALLED; unused days are credited back.
   */
  async earlyReturn(
    id: number,
    payload: LeaveRecallRequest,
  ): Promise<LeaveApplicationDetails> {
    const response = await api.post<LeaveApplicationDetails>(
      `/api/hr/leave/applications/${id}/early-return`,
      payload,
    );
    return response.data;
  },

  async whoIsOnLeave(): Promise<WhoIsOnLeaveResponse> {
    const response = await api.get<WhoIsOnLeaveResponse>(
      "/api/hr/leave/applications/on-leave",
    );
    return response.data;
  },

  async eligibleLeaveTypes(): Promise<EligibleLeaveType[]> {
    const response = await api.get<EligibleLeaveType[]>(
      "/api/hr/leave/types/eligible",
    );
    return response.data;
  },

  async activeLeaveTypes(): Promise<LeaveType[]> {
    const response = await api.get<LeaveType[]>("/api/hr/leave/types/active");
    return response.data;
  },

  async allLeaveTypes(): Promise<LeaveType[]> {
    const response = await api.get<LeaveType[]>("/api/hr/leave/types");
    return response.data;
  },

  async createLeaveType(payload: LeaveTypeCreateRequest): Promise<LeaveType> {
    const response = await api.post<LeaveType>("/api/hr/leave/types", payload);
    return response.data;
  },

  async updateLeaveType(
    id: number,
    payload: LeaveTypeUpdateRequest,
  ): Promise<LeaveType> {
    const response = await api.put<LeaveType>(
      `/api/hr/leave/types/${id}`,
      payload,
    );
    return response.data;
  },

  async activateLeaveType(id: number): Promise<LeaveType> {
    const response = await api.put<LeaveType>(
      `/api/hr/leave/types/${id}/activate`,
    );
    return response.data;
  },

  async deactivateLeaveType(id: number): Promise<LeaveType> {
    const response = await api.put<LeaveType>(
      `/api/hr/leave/types/${id}/deactivate`,
    );
    return response.data;
  },

  async myDashboardSummary(): Promise<LeaveDashboardSummaryResponse> {
    const response = await api.get<LeaveDashboardSummaryResponse>(
      "/api/hr/leave/balances/my-summary",
    );
    return response.data;
  },

  async myBalances(leaveYear?: number): Promise<LeaveBalance[]> {
    const employee = await this.currentEmployee();
    return this.employeeBalances(employee.id, leaveYear);
  },

  async employeeBalances(
    employeeId: number,
    leaveYear?: number,
  ): Promise<LeaveBalance[]> {
    const query = buildQuery({ employeeId, leaveYear });
    const response = await api.get<LeaveBalance[]>(
      `/api/hr/leave/balances?${query}`,
    );
    return response.data;
  },

  async balanceMatrix(
    leaveYear?: number,
    page = 0,
    size = 10,
    search?: string,
  ): Promise<LeaveBalanceMatrixResponse> {
    const query = buildQuery({ leaveYear, page, size, search });
    const response = await api.get<LeaveBalanceMatrixResponse>(
      `/api/hr/leave/balances/matrix?${query}`,
    );
    return response.data;
  },

  /**
   * Manually sets an employee's entitled + brought-forward days for one leave
   * type/year (creates the balance row if it doesn't exist yet). For one-off
   * opening-balance loads and corrections -- the year-end rollover remains the
   * normal way balances get seeded.
   */
  async adjustLeaveBalance(
    payload: LeaveBalanceAdjustmentRequest,
  ): Promise<LeaveBalance> {
    const response = await api.put<LeaveBalance>(
      "/api/hr/leave/balances/adjust",
      payload,
    );
    return response.data;
  },

  async availableReliefEmployees(
    startDate: string,
    endDate: string,
  ): Promise<ReliefEmployeeOption[]> {
    const query = buildQuery({ startDate, endDate });
    const response = await api.get<ReliefEmployeeOption[]>(
      `/api/hr/leave/relief-employees?${query}`,
    );
    return response.data;
  },

  /** Fetch an attachment blob via the authenticated axios client. */
  async downloadAttachment(url: string): Promise<{ blob: Blob; contentType: string }> {
    const response = await api.get<Blob>(url, { responseType: "blob" });
    const contentType = (response.headers as Record<string, string>)["content-type"] ?? "application/octet-stream";
    return { blob: response.data as Blob, contentType };
  },

  async holidaysBetween(
    startDate: string,
    endDate: string,
  ): Promise<Holiday[]> {
    const query = buildQuery({ startDate, endDate });
    const response = await api.get<Holiday[]>(
      `/api/hr/leave/holidays?${query}`,
    );
    return response.data;
  },

  async holidays(page = 0, size = 10): Promise<PageResponse<Holiday>> {
    const query = buildQuery({ page, size });
    const response = await api.get(`/api/hr/leave/holidays?${query}`);
    return normalizePage<Holiday>(response.data);
  },

  async createHoliday(payload: HolidayCreateRequest): Promise<Holiday> {
    const response = await api.post<Holiday>("/api/hr/leave/holidays", payload);
    return response.data;
  },

  async updateHoliday(
    id: number,
    payload: HolidayUpdateRequest,
  ): Promise<Holiday> {
    const response = await api.put<Holiday>(
      `/api/hr/leave/holidays/${id}`,
      payload,
    );
    return response.data;
  },

  async activateHoliday(id: number): Promise<Holiday> {
    const response = await api.put<Holiday>(
      `/api/hr/leave/holidays/${id}/activate`,
    );
    return response.data;
  },

  async deactivateHoliday(id: number): Promise<Holiday> {
    const response = await api.put<Holiday>(
      `/api/hr/leave/holidays/${id}/deactivate`,
    );
    return response.data;
  },

  async uploadAttachment(file: File): Promise<{
    storagePath: string;
    originalFileName: string;
    contentType: string;
    fileSizeBytes: number;
  }> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post<{
      storagePath: string;
      originalFileName: string;
      contentType: string;
      fileSizeBytes: number;
    }>(
      "/api/hr/leave/attachments",
      formData,
      { headers: { "Content-Type": undefined } },
    );
    return response.data;
  },

  async uploadDraftAttachment(id: number, file: File): Promise<{
    storagePath: string;
    originalFileName: string;
    contentType: string;
    fileSizeBytes: number;
  }> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post<{
      storagePath: string;
      originalFileName: string;
      contentType: string;
      fileSizeBytes: number;
    }>(
      `/api/hr/leave/applications/${id}/attachment`,
      formData,
      { headers: { "Content-Type": undefined } },
    );
    return response.data;
  },

  async importHolidays(file: File): Promise<HolidayImportResponse> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await api.post<HolidayImportResponse>(
      "/api/hr/leave/holidays/import",
      formData,
      { headers: { "Content-Type": undefined } },
    );

    return response.data;
  },

  async entitlementRules(): Promise<LeaveEntitlementRule[]> {
    const response = await api.get<LeaveEntitlementRule[]>(
      "/api/hr/leave/entitlement-rules",
    );
    return response.data;
  },

  async createEntitlementRule(
    payload: LeaveEntitlementRuleCreateRequest,
  ): Promise<LeaveEntitlementRule> {
    const response = await api.post<LeaveEntitlementRule>(
      "/api/hr/leave/entitlement-rules",
      payload,
    );
    return response.data;
  },

  async updateEntitlementRule(
    id: number,
    payload: LeaveEntitlementRuleUpdateRequest,
  ): Promise<LeaveEntitlementRule> {
    const response = await api.put<LeaveEntitlementRule>(
      `/api/hr/leave/entitlement-rules/${id}`,
      payload,
    );
    return response.data;
  },

  async activateEntitlementRule(id: number): Promise<LeaveEntitlementRule> {
    const response = await api.put<LeaveEntitlementRule>(
      `/api/hr/leave/entitlement-rules/${id}/activate`,
    );
    return response.data;
  },

  async deactivateEntitlementRule(id: number): Promise<LeaveEntitlementRule> {
    const response = await api.put<LeaveEntitlementRule>(
      `/api/hr/leave/entitlement-rules/${id}/deactivate`,
    );
    return response.data;
  },

  async leaveYearPolicies(): Promise<LeaveYearPolicy[]> {
    const response = await api.get<LeaveYearPolicy[]>(
      "/api/hr/leave/year-policies",
    );
    return response.data;
  },

  async createLeaveYearPolicy(
    payload: LeaveYearPolicyCreateRequest,
  ): Promise<LeaveYearPolicy> {
    const response = await api.post<LeaveYearPolicy>(
      "/api/hr/leave/year-policies",
      payload,
    );
    return response.data;
  },

  async updateLeaveYearPolicy(
    id: number,
    payload: LeaveYearPolicyUpdateRequest,
  ): Promise<LeaveYearPolicy> {
    const response = await api.put<LeaveYearPolicy>(
      `/api/hr/leave/year-policies/${id}`,
      payload,
    );
    return response.data;
  },

  async activateLeaveYearPolicy(id: number): Promise<LeaveYearPolicy> {
    const response = await api.put<LeaveYearPolicy>(
      `/api/hr/leave/year-policies/${id}/activate`,
    );
    return response.data;
  },

  async deactivateLeaveYearPolicy(id: number): Promise<LeaveYearPolicy> {
    const response = await api.put<LeaveYearPolicy>(
      `/api/hr/leave/year-policies/${id}/deactivate`,
    );
    return response.data;
  },

  async closeLeaveYearPolicy(id: number): Promise<LeaveYearPolicy> {
    const response = await api.put<LeaveYearPolicy>(
      `/api/hr/leave/year-policies/${id}/close`,
    );
    return response.data;
  },

  async reopenLeaveYearPolicy(id: number): Promise<LeaveYearPolicy> {
    const response = await api.put<LeaveYearPolicy>(
      `/api/hr/leave/year-policies/${id}/reopen`,
    );
    return response.data;
  },

  /**
   * Manually triggers the leave-year rollover (creates next-year balances for every
   * active employee). Safe to call more than once -- existing balances/policies are
   * skipped. Normally runs automatically on Jan 1; this is the on-demand equivalent.
   */
  async triggerLeaveYearRollover(fromYear?: number): Promise<LeaveYearRolloverResult> {
    const query = fromYear ? `?fromYear=${fromYear}` : "";
    const response = await api.post<LeaveYearRolloverResult>(
      `/api/hr/leave/year-policies/rollover${query}`,
    );
    return response.data;
  },

  async myLeavePlans(planYear?: number): Promise<LeavePlan[]> {
    const query = buildQuery({ planYear });
    const response = await api.get<LeavePlan[]>(
      `/api/hr/leave/plans/mine${query ? `?${query}` : ""}`,
    );
    return response.data;
  },

  async leavePlansByYear(
    planYear: number,
    status?: LeavePlanStatus,
  ): Promise<LeavePlan[]> {
    const query = buildQuery({ planYear, status });
    const response = await api.get<LeavePlan[]>(
      `/api/hr/leave/plans?${query}`,
    );
    return response.data;
  },

  async getLeavePlan(id: number): Promise<LeavePlan> {
    const response = await api.get<LeavePlan>(`/api/hr/leave/plans/${id}`);
    return response.data;
  },

  async createLeavePlan(payload: LeavePlanCreateRequest): Promise<LeavePlan> {
    const response = await api.post<LeavePlan>("/api/hr/leave/plans", payload);
    return response.data;
  },

  async updateLeavePlan(
    id: number,
    payload: LeavePlanUpdateRequest,
  ): Promise<LeavePlan> {
    const response = await api.put<LeavePlan>(
      `/api/hr/leave/plans/${id}`,
      payload,
    );
    return response.data;
  },

  async cancelLeavePlan(id: number): Promise<LeavePlan> {
    const response = await api.put<LeavePlan>(
      `/api/hr/leave/plans/${id}/cancel`,
    );
    return response.data;
  },

  async markLeavePlanSubmitted(id: number): Promise<LeavePlan> {
    const response = await api.put<LeavePlan>(
      `/api/hr/leave/plans/${id}/mark-submitted`,
    );
    return response.data;
  },

  // ─── Official Duty ────────────────────────────────────────────────────────

  /** Paginated list of all official duty records. Readable by all leave roles. */
  async officialDutyList(
    page = 0,
    size = 20,
    startDate?: string,
    endDate?: string,
  ): Promise<PageResponse<OfficialDutyResponse>> {
    const query = buildQuery({ page, size, startDate, endDate });
    const response = await api.get(`/api/hr/leave/official-duty?${query}`);
    return normalizePage<OfficialDutyResponse>(response.data);
  },

  /** Employees with upcoming official duty (next 30 days). */
  async officialDutyUpcoming(): Promise<OfficialDutyResponse[]> {
    const response = await api.get<OfficialDutyResponse[]>(
      "/api/hr/leave/official-duty/upcoming",
    );
    return response.data;
  },

  /** Create a new official duty record. Requires HR_LEAVE_MANAGE. */
  async createOfficialDuty(
    payload: OfficialDutyCreateRequest,
  ): Promise<OfficialDutyResponse> {
    const response = await api.post<OfficialDutyResponse>(
      "/api/hr/leave/official-duty",
      payload,
    );
    return response.data;
  },

  /** Update an official duty record. Requires HR_LEAVE_MANAGE. */
  async updateOfficialDuty(
    id: number,
    payload: OfficialDutyUpdateRequest,
  ): Promise<OfficialDutyResponse> {
    const response = await api.put<OfficialDutyResponse>(
      `/api/hr/leave/official-duty/${id}`,
      payload,
    );
    return response.data;
  },

  /** Delete an official duty record. Requires HR_LEAVE_MANAGE. */
  async deleteOfficialDuty(id: number): Promise<void> {
    await api.delete(`/api/hr/leave/official-duty/${id}`);
  },
};